import type { KeyedMutator } from 'swr';
import { AnimatePresence, motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import type { Community } from '@/types';

interface PrivacySettingsProps {
  communityId: string;
  mutateCommunity: KeyedMutator<{ community: Community }>;
  customPrivacy: 'OPEN' | 'INVITE_ONLY' | 'CLOSED';
  setCustomPrivacy: (val: 'OPEN' | 'INVITE_ONLY' | 'CLOSED') => void;
  requiresApproval: boolean;
  setRequiresApproval: (val: boolean) => void;
  inviteCodeEnabled: boolean;
  setInviteCodeEnabled: (val: boolean) => void;
  searchVisible: boolean;
  setSearchVisible: (val: boolean) => void;
}

export function PrivacySettings({
  communityId,
  mutateCommunity,
  customPrivacy,
  setCustomPrivacy,
  requiresApproval,
  setRequiresApproval,
  inviteCodeEnabled,
  setInviteCodeEnabled,
  searchVisible,
  setSearchVisible,
}: PrivacySettingsProps) {
  const updateSetting = async (key: string, value: unknown) => {
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        [key]: value,
      });
      await mutateCommunity();
      toast.success('Settings updated');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update settings');
    }
  };

  const handlePrivacyChange = async (newPrivacy: 'OPEN' | 'INVITE_ONLY') => {
    setCustomPrivacy(newPrivacy);
    await updateSetting('customPrivacy', newPrivacy);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Membership Type</CardTitle>
          <CardDescription>Control how members can join your community.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-2">
            <div
              className={cn(
                'rounded-2xl border text-left transition-all w-full overflow-hidden',
                customPrivacy === 'OPEN' ? 'border-primary shadow-sm' : 'border-border/40'
              )}
            >
              <button
                type="button"
                onClick={() => handlePrivacyChange('OPEN')}
                className={cn(
                  'flex flex-col items-start p-3 text-left w-full transition-all',
                  customPrivacy === 'OPEN'
                    ? 'bg-primary/10 text-foreground'
                    : 'bg-accent/5 text-muted-foreground hover:bg-accent/10'
                )}
              >
                <span className="font-extrabold text-sm text-foreground">Public (Open)</span>
                <span className="text-xs font-medium text-muted-foreground mt-0.5">
                  Anyone can view and join your community.
                </span>
              </button>
              <AnimatePresence initial={false}>
                {customPrivacy === 'OPEN' && (
                  <motion.div
                    key="open-options"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 py-3 bg-primary/5 border-t border-primary/20 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-bold text-foreground">
                            Require Approval
                          </Label>
                          <p className="text-xs text-muted-foreground font-medium pr-[17px]">
                            New members must be approved by an admin before accessing.
                          </p>
                        </div>
                        <Switch
                          checked={requiresApproval}
                          onCheckedChange={(checked) => {
                            setRequiresApproval(checked);
                            updateSetting('requiresApproval', checked);
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div
              className={cn(
                'rounded-2xl border text-left transition-all w-full overflow-hidden',
                customPrivacy === 'INVITE_ONLY' ? 'border-primary shadow-sm' : 'border-border/40'
              )}
            >
              <button
                type="button"
                onClick={() => handlePrivacyChange('INVITE_ONLY')}
                className={cn(
                  'flex flex-col items-start p-3 text-left w-full transition-all',
                  customPrivacy === 'INVITE_ONLY'
                    ? 'bg-primary/10 text-foreground'
                    : 'bg-accent/5 text-muted-foreground hover:bg-accent/10'
                )}
              >
                <span className="font-extrabold text-sm text-foreground">Private</span>
                <span className="text-xs font-medium text-muted-foreground mt-0.5">
                  Only people with an invite link can join.
                </span>
              </button>
              <AnimatePresence initial={false}>
                {customPrivacy === 'INVITE_ONLY' && (
                  <motion.div
                    key="private-options"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 py-3 bg-primary/5 border-t border-primary/20 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-bold text-foreground">
                            Enable Invite Code &amp; Link
                          </Label>
                          <p className="text-xs text-muted-foreground font-medium pr-[17px]">
                            Allow members to join using the community invite code or invite link.
                          </p>
                        </div>
                        <Switch
                          checked={inviteCodeEnabled}
                          onCheckedChange={(checked) => {
                            setInviteCodeEnabled(checked);
                            updateSetting('inviteCodeEnabled', checked);
                          }}
                        />
                      </div>
                      <div className="flex items-center justify-between border-t border-primary/10 pt-3">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-bold text-foreground">
                            Require Approval
                          </Label>
                          <p className="text-xs text-muted-foreground font-medium pr-[17px]">
                            Members joining via invite code need admin approval. Directly invited
                            members are auto-approved.
                          </p>
                        </div>
                        <Switch
                          checked={requiresApproval}
                          onCheckedChange={(checked) => {
                            setRequiresApproval(checked);
                            updateSetting('requiresApproval', checked);
                          }}
                        />
                      </div>
                      <div className="flex items-center justify-between border-t border-primary/10 pt-3">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-bold text-foreground">
                            Search Visibility
                          </Label>
                          <p className="text-xs text-muted-foreground font-medium pr-[17px]">
                            Allow this community to appear in public search when users search using
                            the exact name of the community.
                          </p>
                        </div>
                        <Switch
                          checked={searchVisible}
                          onCheckedChange={(checked) => {
                            setSearchVisible(checked);
                            updateSetting('searchVisible', checked);
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
